import is from '@sindresorhus/is';
import { useQueryClient } from '@tanstack/react-query';
import { type FunctionComponent, type ReactNode, useEffect, useRef, useState } from 'react';

import type { ContextRefreshAction, ContextRefreshEvent, EntityIdentifier } from '../../data-contract/chat';
import { jobArchitectureKeys } from '../../job-architecture-v2/api/query-keys';
import { useChatComposerActive } from '../chat-composer-contexts';
import type { ChatContext } from '../chat-context-types';
import { JobItemVersionRedirect } from './job-item-version-redirect';

const matchesContext = (entity: EntityIdentifier, context: ChatContext): boolean => {
    if (context.kind !== 'job-profile') return false;
    if (entity.jobArchitectureId !== context.architectureId) return false;
    if (entity.id !== context.itemId) return false;
    if (context.itemType === 'job-role') return entity.type === 'role';
    return entity.type === 'level';
};

const matchesAnyActiveContext = (entity: EntityIdentifier, activeContexts: readonly ChatContext[]): boolean =>
    activeContexts.some((context) => matchesContext(entity, context));

const renderEntityEffect = (
    entity: EntityIdentifier,
    action: ContextRefreshAction,
    activeContexts: readonly ChatContext[],
    key: string,
): ReactNode => {
    if (action === 'updated' && matchesAnyActiveContext(entity, activeContexts)) {
        return <JobItemVersionRedirect key={key} entity={entity} />;
    }
    if ((action === 'created' || action === 'unarchived') && activeContexts.length === 0) {
        // TODO: render <OpenItemSideEffect /> once implemented.
        return null;
    }
    return null;
};

export const ContextRefreshSideEffect: FunctionComponent<{ readonly envelope: ContextRefreshEvent }> = ({
    envelope,
}) => {
    const queryClient = useQueryClient();
    const { activeChatContexts } = useChatComposerActive();
    const [activeAtMount] = useState(activeChatContexts);
    const hasInvalidatedRef = useRef(false);

    // biome-ignore lint/correctness/useExhaustiveDependencies: cache invalidation runs once per envelope mount.
    useEffect(() => {
        if (hasInvalidatedRef.current) return;
        hasInvalidatedRef.current = true;
        for (const entity of envelope.payload.entities) {
            if (is.nonEmptyString(entity.jobArchitectureId)) {
                void queryClient.invalidateQueries({
                    queryKey: jobArchitectureKeys.architecture(entity.jobArchitectureId),
                });
            }
        }
    }, []);

    return (
        <>
            {envelope.payload.entities.map((entity, index) =>
                renderEntityEffect(entity, envelope.payload.action, activeAtMount, `${envelope.eventId}-${index}`),
            )}
        </>
    );
};
